import React, { useEffect, useState } from 'react';
import './reference.scss';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';
import { isMobile } from 'react-device-detect';
import {
  getFilterData,
  getPaginationOpts,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import { loadUserActionById, loadUserActionLogList } from '../actions';
import { getSystemData } from '../../System/reducer';
import { getReferenceInformationData } from '../reducer';
import Pagination from '../../../components/base/Pagination/Pagination';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import { dateTimeFormatter, subjectFormatter, userPositionFormatter } from '../../../utils/formatters';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import Grid from '../../../components/base/Grid/Grid';
import getDefaultDataRange from '../../../utils/getDefaultDataRange';
import ReferenceHistoryDialog from './ReferenceHistoryDialog';
import { REFERENCE_INFORMATION_MODULE } from '../../../configs/moduleNames';
import getDefaultSort from '../../../utils/getDefaultSort';
import { PAGE_TYPE } from '../../FilterTemplate/components/filter-settings';
import TimerLoader from '../../../components/base/Loader/TimerLoader';
import FilterModal from '../../FilterTemplate/components/FilterModal';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import TextSearchInput from '../../../components/fields/TextInput/TextSearchInput';
import RangeDatePicker from '../../../components/fields/RangeDatePicker/RangeDatePicker';
import UserActionLogDTO from '../../../dto/UserActionLogDTO';
import ExportInterface from '../../System/components/ExportInterface';

export default function UserActivityLogPage() {
  const dispatch = useDispatch();
  const store = useStore();
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const subject_id = searchParams.get('subject_id');
  const [isOpenFilter, setOpenFilter] = useState(false);
  const { updatePeriod } = useSelector(getSystemData);
  const userActionLogDto = new UserActionLogDTO();
  const userActionLogAttributes = userActionLogDto.getAttributesByName();
  const {
    userActivityLogListMeta = {},
    loadingUserActivityLogList,
    userActivityLogList = [],
    currentUserActivity,
    loadingCurrentUserActivity = false,
  } = useSelector(getReferenceInformationData);

  const defaultDate = getDefaultDataRange();
  const { formatMessage } = useIntl();
  const {
    getValues, control, watch, setValue, reset,
  } = useForm({ defaultValues: getFilterData('ual-filter', {}) });
  const userActivityLogColumns = [
    {
      dataField: 'causer',
      text: 'Пользователь',
      headerClassName: 'user-column',
      formatter: userPositionFormatter,
    },
    {
      dataField: 'created_at',
      text: 'Дата и время',
      headerClassName: 'date-time-column',
      formatter: dateTimeFormatter,
    },
    {
      dataField: 'subject',
      text: 'Объект',
      headerClassName: 'module-column',
      formatter: (_, item) => subjectFormatter(item),
    },
    {
      dataField: 'description',
      text: 'Изменения в объекте',
      headerClassName: 'changes-to-object-column',
    },
  ];

  const [sort, setSort] = useState(getDefaultSort(userActivityLogColumns));
  const loadData = opts => {
    const filterData = getValues();
    const paginationOpts = getPaginationOpts(store.getState()[REFERENCE_INFORMATION_MODULE].userActivityLogListMeta, opts); // костыль внедрен из за потери контекста в updatePeriod. Он сохранял первую функцию
    const requestData = {
      ...paginationOpts,
      filter: filterData,
      sort,
    };
    const p1 = dispatch(loadUserActionLogList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  useEffect(() => setValue('subject_id', subject_id), [subject_id]);

  const handleSelectRow = item => {
    if (item?.log_name && item?.log_name.includes('_link')) {
      return;
    }
    dispatch(loadUserActionById(item.id)).then(data => {
      setSelectedRow(item);
    }).catch(responseLogWithNotification);
  };

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  useEffect(() => {
    const subscription = watch(values => loadData({ page: 1 }));
    return () => subscription.unsubscribe();
  }, [watch]);

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  useEffect(() => { loadData(); }, [sort]);

  return (
    <div className="page ual-page">
      <TimerLoader loading={loadingUserActivityLogList} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.reference.user.action' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextSearchInput}
            dtoObject={{
              name: 'search',
              type: String,
              labelKey: 'search',
            }}
            className="no-border"
          />
          <FieldDTO
            control={control}
            component={RangeDatePicker}
            dtoObject={userActionLogAttributes.created_at}
            usePortal
            changeFormat="YYYY-MM-DD"
            className="no-border"
            placeholder="Дата"
          />
          {!isMobile && isOpenFilter
            ? (
              <FilterModal
                control={control}
                onClose={() => setOpenFilter(false)}
                getValues={getValues}
                reset={reset}
                defaultValues={{}}
                page={PAGE_TYPE.REFERENCE_USER_ACTION}
              />
            )
            : null}
          <div className="filter-sep" />
          <ExportInterface page="activity_logs" exporter="activity_logs" getValues={getValues} />
          <div className="filter-sep" />
          <Button variant="second" onClick={() => setOpenFilter(true)}>
            <Icons customIcon className="file-download" width={24} height={24} icon="filter" />
          </Button>
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content">
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <Grid
                onDoubleClickByRow={handleSelectRow}
                rowClassFormatter={item => (selectedRow && item.id === selectedRow.id ? 'clickable-table-row selected' : 'clickable-table-row')}
                data={userActivityLogList}
                columns={userActivityLogColumns}
                name="ual-grid"
                className="ual-grid"
                changeSort={changeSort}
                sort={sort}
              />
            </div>
          </div>
          <Pagination meta={userActivityLogListMeta} list={userActivityLogList} handleChangeMeta={loadData} />
        </div>
      </div>
      <TimerLoader loading={loadingCurrentUserActivity} />
      { selectedRow && currentUserActivity ? <ReferenceHistoryDialog close={() => setSelectedRow(null)} /> : null }
    </div>
  );
}
